import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus, Search, Users, Phone, MessageSquare, TrendingUp,
  X, SlidersHorizontal, ChevronRight as ChevronRightIcon,
} from 'lucide-react'
import {
  Card,
  CardContent,
  AlertMessage,
  Toast,
  ToastType,
  Drawer,
  AutocompleteInput,
  ModernInput,
  ModernSelect,
  ListPageSkeleton,
  KanbanSkeleton,
  StatsBarSkeleton,
} from '../../components/ui'
import { LeadFilters } from '../../components/filters'
import { LeadsKanbanView, LeadsListView, ViewToggle, LeadDrawer } from '../../components/leads'
import { Lead, Vehicle } from '../../types'
import { useLeads } from '../../hooks/useLeads'
import { useSalesFunnels } from '../../hooks/useSalesFunnels'
import { useLeadOptions } from '../../hooks/useLeadOptions'
import { useUsers } from '../../hooks/useUsers'
import { useVehicles } from '../../hooks/useVehicles'
import { cn } from '../../utils/cn'
import { apiService, ApiError } from '../../services/api'

type TriStateFilter = 'all' | 'true' | 'false'

export const LeadsPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { leads, isLoading, error, pagination, fetchLeads, deleteLead, updateLead, updateLeadStage } = useLeads()
  const { defaultFunnel, isLoading: isFunnelLoading } = useSalesFunnels()
  const { options: leadOptions } = useLeadOptions()
  const { users, isLoading: isUsersLoading } = useUsers()
  const { isLoading: isVehiclesLoading } = useVehicles()

 const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban')
 const [searchTerm, setSearchTerm] = useState('')
 const [nameFilter, setNameFilter] = useState('')
 const [phoneFilter, setPhoneFilter] = useState('')
 const [emailFilter, setEmailFilter] = useState('')
 const [documentFilter, setDocumentFilter] = useState('')
 const [statusFilter, setStatusFilter] = useState<string>('all')
 const [sourceFilter, setSourceFilter] = useState<string>('all')
 const [assignedToFilter, setAssignedToFilter] = useState('')
 const [assignedToFilterLabel, setAssignedToFilterLabel] = useState('')
 const [vehicleInterestFilter, setVehicleInterestFilter] = useState('')
 const [funnelStageFilter, setFunnelStageFilter] = useState<string>('all')
 const [createdAtFromFilter, setCreatedAtFromFilter] = useState('')
 const [createdAtToFilter, setCreatedAtToFilter] = useState('')
const [hasProposalFilter, setHasProposalFilter] = useState<TriStateFilter>('all')
  const [isConvertedFilter, setIsConvertedFilter] = useState<TriStateFilter>('all')
 const [filtersPanelOpen, setFiltersPanelOpen] = useState(true)
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerLead, setDrawerLead] = useState<Lead | null>(null)

const [vehicleSearchTerm, setVehicleSearchTerm] = useState('')
  const [vehicleSearchResults, setVehicleSearchResults] = useState<Vehicle[]>([])

  const userComboboxOptions = useMemo(
   () =>
    users
     .filter((user) => (user.isActive !== undefined ? user.isActive : user.status === 'active'))
     .map((user) => ({
      id: user.id,
      label: user.name,
      meta: user.email,
     })),
   [users]
  )

  const vehicleComboboxOptions = useMemo(
   () =>
    vehicleSearchResults.map((v) => ({
     id: v.id,
     label: `${v.brand} ${v.model} ${v.version || ''}`.trim(),
     meta: (v as any).plate || (v as any).chassis || '',
    })),
   [vehicleSearchResults]
  )

  const firstFunnelStageId = useMemo(() => {
    if (!defaultFunnel?.stages?.length) return null
    const sorted = [...defaultFunnel.stages].sort((a, b) => a.order - b.order)
    return sorted[0]?.id ?? null
  }, [defaultFunnel])

useEffect(() => {
    const timer = setTimeout(() => {
      if (vehicleSearchTerm.trim().length >= 3) {
        apiService.getVehicles({ name: vehicleSearchTerm, limit: 20, status: 'in_stock' }).then((res) => {
      if (res && 'vehicles' in res) {
       setVehicleSearchResults(res.vehicles || [])
      } else if (Array.isArray(res)) {
       setVehicleSearchResults(res)
      } else {
       setVehicleSearchResults([])
      }
     }).catch(() => setVehicleSearchResults([]))
    } else {
     setVehicleSearchResults([])
    }
   }, 500)
   return () => clearTimeout(timer)
  }, [vehicleSearchTerm])

  const formatPhoneMask = (value: string) => {
   const digits = value.replace(/\D/g, '')
   if (digits.length <= 11) {
    let masked = ''
    if (digits.length > 0) masked = `(${digits.slice(0, 2)}`
    if (digits.length >= 3) masked += `) ${digits.slice(2, 7)}`
    if (digits.length >= 7) masked += `-${digits.slice(7, 11)}`
    return masked
   }
   return value
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
   const masked = formatPhoneMask(e.target.value)
   setPhoneFilter(masked)
  }

  const buildLeadParams = (page: number = 1) => {
 const params: any = { page, limit: 50 }
 const safeSearch = searchTerm.trim()
 const safeName = nameFilter.trim()
 const safePhone = phoneFilter.trim()
 const safeEmail = emailFilter.trim()
 const safeDocument = documentFilter.trim()
 const safeAssignedTo = assignedToFilter.trim()
 const safeVehicleInterest = vehicleInterestFilter.trim()

if (statusFilter !== 'all') params.status = statusFilter
  if (sourceFilter !== 'all') params.source = sourceFilter
  if (funnelStageFilter !== 'all') params.funnelStage = funnelStageFilter
  if (hasProposalFilter !== 'all') params.hasProposal = hasProposalFilter === 'true'
  if (isConvertedFilter !== 'all') params.isConverted = isConvertedFilter === 'true'
 if (safeSearch) params.search = safeSearch
 if (safeName) params.name = safeName
 if (safePhone) params.phone = safePhone
 if (safeEmail) params.email = safeEmail
 if (safeDocument) params.document = safeDocument
 if (safeAssignedTo) params.assignedTo = safeAssignedTo
 if (safeVehicleInterest) params.vehicleInterestId = safeVehicleInterest
 if (createdAtFromFilter) params.createdAtFrom = createdAtFromFilter
 if (createdAtToFilter) params.createdAtTo = createdAtToFilter

 return params
 }

 useEffect(() => {
 const params = buildLeadParams(1)
 const hasTextFilter =
 !!searchTerm.trim() ||
 !!nameFilter.trim() ||
 !!phoneFilter.trim() ||
 !!emailFilter.trim() ||
 !!documentFilter.trim() ||
 !!vehicleInterestFilter.trim()

 const timer = setTimeout(() => fetchLeads(params), hasTextFilter ? 500 : 0)
 return () => clearTimeout(timer)
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [
 searchTerm,
 nameFilter,
 phoneFilter,
 emailFilter,
 documentFilter,
 statusFilter,
 sourceFilter,
 assignedToFilter,
 vehicleInterestFilter,
 funnelStageFilter,
hasProposalFilter,
  isConvertedFilter,
 createdAtFromFilter,
 createdAtToFilter,
 ])

 const handleCreateLead = () => navigate('/leads/new')
 const handleEditLead = (lead: Lead) => navigate(`/leads/edit/${lead.id}`)
 const handleOpenDrawer = (lead: Lead) => { setDrawerLead(lead); setIsDrawerOpen(true) }
 const handleCloseDrawer = () => { setIsDrawerOpen(false); setDrawerLead(null) }
 const handleEditFromDrawer = () => { if (drawerLead) navigate(`/leads/edit/${drawerLead.id}`) }

 const handleDelete = async (id: string) => {
 if (confirm('Tem certeza que deseja excluir este lead?')) {
 try {
 await deleteLead(id)
 setToast({ message: 'Lead excluído com sucesso!', type: 'success' })
 } catch {
 setToast({ message: 'Erro ao excluir lead. Tente novamente.', type: 'error' })
 }
 }
 }

 const handleUpdateLeadStage = async (leadId: string, stageId: string) => {
 try {
 await updateLeadStage(leadId, stageId)
 setToast({ message: t('leads.kanban.stageUpdated'), type: 'success' })
 } catch (err) {
 const fallback = t('leads.kanban.stageUpdateFailed')
 const message = err instanceof ApiError && err.message ? err.message : fallback
 setToast({ message, type: 'error' })
 }
}

 const handleAdvanceStageBlocked = () => {
 setToast({ message: t('leads.kanban.advanceRequiresAssignee'), type: 'warning' })
 }

 const handleDrawerAssignSalesperson = async (leadId: string, userId: string) => {
  try {
   const updated = await updateLead(leadId, {
    assignedTo: userId.trim() === '' ? null : userId,
   })
   if (updated) {
    setDrawerLead((prev) => (prev?.id === leadId ? updated : prev))
   }
   setToast({ message: t('leads.drawer.assigneeUpdated'), type: 'success' })
  } catch {
   setToast({ message: t('leads.drawer.assigneeUpdateFailed'), type: 'error' })
  }
 }

 const handleContactLead = async (
  leadId: string,
  channel: 'whatsapp' | 'phone' | 'email',
  target: string
 ) => {
  await updateLead(leadId, {
   status: 'contacted',
   contactChannel: channel,
   contactTarget: target,
  } as any)
 }

 const getStatusCount = (statusValue: string) =>
 leads.filter((l) => l.status === statusValue).length

 const hasKanban =
 !isFunnelLoading &&
 defaultFunnel !== null &&
 defaultFunnel.stages &&
 defaultFunnel.stages.length > 0

const hasActiveFilters =
  statusFilter !== 'all' ||
  sourceFilter !== 'all' ||
  funnelStageFilter !== 'all' ||
  hasProposalFilter !== 'all' ||
  isConvertedFilter !== 'all' ||
 !!searchTerm.trim() ||
 !!nameFilter.trim() ||
 !!phoneFilter.trim() ||
 !!emailFilter.trim() ||
 !!documentFilter.trim() ||
 !!assignedToFilter ||
 !!vehicleInterestFilter.trim() ||
 !!createdAtFromFilter ||
 !!createdAtToFilter

const clearAllFilters = () => {
  setSearchTerm('')
  setNameFilter('')
  setPhoneFilter('')
  setEmailFilter('')
  setDocumentFilter('')
  setStatusFilter('all')
  setSourceFilter('all')
  setAssignedToFilter('')
  setAssignedToFilterLabel('')
  setVehicleInterestFilter('')
  setVehicleSearchTerm('')
  setVehicleSearchResults([])
  setFunnelStageFilter('all')
  setCreatedAtFromFilter('')
  setCreatedAtToFilter('')
  setHasProposalFilter('all')
  setIsConvertedFilter('all')
  }

 const statIcons = [Users, Phone, MessageSquare, TrendingUp]
 const statColors = [
 'text-accent-600 bg-accent-50 dark:bg-accent-900/20',
 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
 'text-purple-600 bg-purple-50 dark:bg-purple-900/20',
 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20',
 ]

 return (
 <div className="space-y-5">

 {/* ── Page header ── */}
 <div className="flex items-start justify-between gap-4">
 <div>
 <h1
 className="text-xl font-bold text-foreground sm:text-2xl"
 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
 >
 Leads
 </h1>
 <p className="mt-0.5 text-sm text-muted-foreground">
 {pagination.total > 0
 ? `${pagination.total} leads no total`
 : 'Gerencie seus leads e acompanhe o funil'}
 </p>
 </div>

 <div className="flex items-center gap-2 flex-shrink-0">
 {hasKanban && (
 <ViewToggle view={viewMode} onViewChange={setViewMode} hasKanban={hasKanban} />
 )}
 <button
 type="button"
 onClick={handleCreateLead}
 className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-accent-600 text-white hover:bg-accent-700 active:scale-[0.98] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 shadow-sm"
 >
 <Plus className="h-4 w-4" />
 <span className="hidden sm:inline">Novo Lead</span>
 </button>
 </div>
 </div>

 {/* ── Alerts ── */}
 {error && <AlertMessage variant="error" message={error} />}

 {/* ── Stats ── */}
 {isLoading ? (
 <StatsBarSkeleton count={4} />
 ) : (
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
 {leadOptions.statuses.slice(0, 4).map((status, i) => {
 const Icon = statIcons[i] || Users
 const colorClasses = statColors[i] || statColors[0]
 return (
 <div
 key={status.value}
 className="bg-card border border-border rounded-lg p-4 shadow-precision"
 >
 <div className="flex items-center justify-between mb-3">
 <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground truncate pr-2">
 {status.label}
 </p>
 <div className={cn('p-1.5 rounded-md flex-shrink-0', colorClasses.split(' ').slice(1).join(' '))}>
 <Icon className={cn('h-3.5 w-3.5', colorClasses.split(' ')[0])} />
 </div>
 </div>
 <p
 className="text-2xl font-semibold text-foreground"
 style={{ fontFamily: '"JetBrains Mono", monospace' }}
 >
 {getStatusCount(status.value)}
 </p>
 </div>
 )
 })}
 </div>
 )}

 {/* ── Main layout: filters + content ── */}
 <div className="flex gap-4 items-start">

 {/* ── Left filters panel ── */}
 <div className={cn(
 'flex-shrink-0 transition-all duration-200',
 filtersPanelOpen ? 'w-56' : 'w-10',
 // On mobile, filters show as a top bar instead
 'hidden lg:block'
 )}>
{filtersPanelOpen ? (
  <LeadFilters
    nameFilter={nameFilter}
    phoneFilter={phoneFilter}
    emailFilter={emailFilter}
    documentFilter={documentFilter}
    statusFilter={statusFilter}
    sourceFilter={sourceFilter}
    funnelStageFilter={funnelStageFilter}
    assignedTo={assignedToFilter}
    assignedToLabel={assignedToFilterLabel}
    vehicleInterest={vehicleInterestFilter}
    vehicleSearchTerm={vehicleSearchTerm}
    createdAtFrom={createdAtFromFilter}
    createdAtTo={createdAtToFilter}
    hasProposalFilter={hasProposalFilter}
    isConvertedFilter={isConvertedFilter}
    leadOptions={leadOptions}
    userOptions={userComboboxOptions}
    isUsersLoading={isUsersLoading}
    vehicleOptions={vehicleComboboxOptions}
    isVehiclesLoading={isVehiclesLoading}
    onNameChange={setNameFilter}
    onPhoneChange={(val) => setPhoneFilter(val)}
    onEmailChange={setEmailFilter}
    onDocumentChange={setDocumentFilter}
    onStatusChange={setStatusFilter}
    onSourceChange={setSourceFilter}
    onFunnelStageChange={setFunnelStageFilter}
    onAssignedToChange={(opt) => {
      setAssignedToFilter(opt.id)
      setAssignedToFilterLabel(opt.label)
    }}
    onAssignedToLabelChange={setAssignedToFilterLabel}
    onVehicleInterestChange={(opt) => setVehicleInterestFilter(opt.id)}
    onVehicleSearchTermChange={setVehicleSearchTerm}
    onCreatedAtFromChange={setCreatedAtFromFilter}
    onCreatedAtToChange={setCreatedAtToFilter}
    onHasProposalChange={(value: string) => setHasProposalFilter(value as TriStateFilter)}
    onIsConvertedChange={(value: string) => setIsConvertedFilter(value as TriStateFilter)}
    onClearFilters={clearAllFilters}
    activeCount={hasActiveFilters ? Object.values({ statusFilter, sourceFilter, funnelStageFilter, assignedToFilter, vehicleInterestFilter, createdAtFromFilter, createdAtToFilter, hasProposalFilter, isConvertedFilter }).filter(v => v && v !== 'all').length : 0}
  />
  ) : (
 /* Collapsed panel — just toggle button */
 <button
 type="button"
 onClick={() => setFiltersPanelOpen(true)}
 className={cn(
 'w-10 flex flex-col items-center gap-2 py-3 rounded-lg border border-border bg-card hover:bg-muted transition-colors',
 hasActiveFilters && 'border-accent-300 dark:border-accent-700'
 )}
 >
 <SlidersHorizontal className={cn('h-4 w-4', hasActiveFilters ? 'text-accent-600' : 'text-muted-foreground')} />
 <ChevronRightIcon className="h-3 w-3 text-muted-foreground" />
 {hasActiveFilters && (
 <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
 )}
 </button>
 )}
 </div>

 {/* ── Content area ── */}
 <div className="flex-1 min-w-0 space-y-3">

 {/* Mobile: search + filters bar */}
 <div className="lg:hidden space-y-2">
 <div className="flex gap-2">
 <div className="relative flex-1">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <ModernInput
 type="search"
 placeholder="Buscar leads..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
               className="w-full pl-9 pr-4 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground placeholder:text-muted-foreground"
 />
 {searchTerm && (
 <button
 type="button"
 onClick={() => setSearchTerm('')}
 className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground"
 >
 <X className="h-3.5 w-3.5" />
 </button>
 )}
 </div>
<ModernSelect
  value={statusFilter}
  onChange={(e) => setStatusFilter(e.target.value)}
  options={[
  { value: 'all', label: t('leads.filters.status') },
  ...leadOptions.statuses.map((s) => ({ value: s.value, label: s.label })),
  ]}
  />
  <ModernSelect
  value={sourceFilter}
  onChange={(e) => setSourceFilter(e.target.value)}
  options={[
  { value: 'all', label: t('leads.filters.source') },
  ...leadOptions.sources.map((s) => ({ value: s.value, label: s.label })),
  ]}
  />
  </div>
             <div className="grid grid-cols-2 gap-2">
              <ModernInput
               type="text"
               placeholder={t('leads.filters.name')}
               value={nameFilter}
               onChange={(e) => setNameFilter(e.target.value)}
               className="px-3 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground placeholder:text-muted-foreground"
              />
              <ModernInput
               type="text"
               placeholder={t('leads.filters.phone')}
               value={phoneFilter}
               onChange={handlePhoneChange}
               className="px-3 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground placeholder:text-muted-foreground"
              />
              <ModernInput
               type="text"
               placeholder={t('leads.filters.email')}
               value={emailFilter}
               onChange={(e) => setEmailFilter(e.target.value)}
               className="px-3 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground placeholder:text-muted-foreground"
              />
              <ModernInput
               type="text"
               placeholder={t('leads.filters.document')}
               value={documentFilter}
               onChange={(e) => setDocumentFilter(e.target.value)}
               className="px-3 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground placeholder:text-muted-foreground"
              />
              <AutocompleteInput
               value={assignedToFilterLabel}
               selectedId={assignedToFilter}
               options={userComboboxOptions}
               loading={isUsersLoading}
               placeholder={t('leads.filters.serviceUser')}
               emptyMessage={t('common.noResults')}
               onChange={setAssignedToFilterLabel}
               onSelect={(option) => {
                setAssignedToFilter(option.id)
                setAssignedToFilterLabel(option.label)
               }}
              />
              <AutocompleteInput
               value={vehicleSearchTerm}
               selectedId={vehicleInterestFilter}
               options={vehicleComboboxOptions}
               loading={isVehiclesLoading}
               placeholder={t('leads.filters.vehicleInterest')}
               emptyMessage={t('common.noResults')}
               onChange={setVehicleSearchTerm}
               onSelect={(option) => {
                setVehicleInterestFilter(option.id)
               }}
              />
              <ModernInput
               type="date"
               value={createdAtFromFilter}
               onChange={(e) => setCreatedAtFromFilter(e.target.value)}
               className="px-3 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground"
              />
              <ModernInput
               type="date"
               value={createdAtToFilter}
               onChange={(e) => setCreatedAtToFilter(e.target.value)}
               className="px-3 py-2 text-sm bg-card border-border rounded-lg text-muted-foreground"
              />
              <ModernSelect
               value={hasProposalFilter}
               onChange={(e) => setHasProposalFilter(e.target.value as TriStateFilter)}
               className="text-sm font-medium text-muted-foreground"
               options={[
                { value: 'all', label: t('leads.filters.hasProposal') + ': ' + t('leads.filters.all') },
                { value: 'true', label: t('leads.filters.hasProposal') + ': ' + t('leads.filters.yes') },
                { value: 'false', label: t('leads.filters.hasProposal') + ': ' + t('leads.filters.no') },
               ]}
              />
              <ModernSelect
               value={isConvertedFilter}
               onChange={(e) => setIsConvertedFilter(e.target.value as TriStateFilter)}
               className="text-sm font-medium text-muted-foreground"
               options={[
                { value: 'all', label: t('leads.filters.converted') + ': ' + t('leads.filters.all') },
                { value: 'true', label: t('leads.filters.converted') + ': ' + t('leads.filters.yes') },
                { value: 'false', label: t('leads.filters.converted') + ': ' + t('leads.filters.no') },
               ]}
              />
{leadOptions.funnelStages.length > 0 && (
               <ModernSelect
                value={funnelStageFilter}
                onChange={(e) => setFunnelStageFilter(e.target.value)}
                options={[
                 { value: 'all', label: t('leads.filters.funnelStage') },
                 ...leadOptions.funnelStages.map((s) => ({ value: s.value, label: s.label })),
                ]}
               />
              )}
            </div>
            {hasActiveFilters && (
             <button
              type="button"
              onClick={clearAllFilters}
              className="w-full py-2 text-sm font-medium text-accent-600 hover:text-accent-700 border border-accent-200 dark:border-accent-800 rounded-lg hover:bg-accent-50 dark:hover:bg-accent-900/10 transition-colors"
             >
{t('leads.filters.clearFilters')}
             </button>
            )}
 </div>

 {/* List header */}
 {viewMode === 'list' && (
 <div className="flex items-center justify-between">
 <p className="text-xs font-medium text-muted-foreground">
 {isLoading
 ? 'Carregando...'
 : `${leads.length} ${leads.length === 1 ? 'lead' : 'leads'}`}
 </p>
 {pagination.totalPages > 1 && (
 <p className="text-xs text-muted-foreground">
 Página {pagination.page} de {pagination.totalPages}
 </p>
 )}
 </div>
 )}

 {/* ── View content ── */}
 {viewMode === 'list' ? (
 <>
 {isLoading ? (
 <ListPageSkeleton variant="table" rows={6} showFilters={false} />
 ) : (
 <LeadsListView
 leads={leads}
 isLoading={isLoading}
 onViewLead={handleOpenDrawer}
 onEditLead={handleEditLead}
 onDeleteLead={handleDelete}
 onAddLead={handleCreateLead}
 searchTerm={searchTerm}
 statusFilter={statusFilter}
 />
 )}

 {/* Pagination */}
 {pagination.totalPages > 1 && (
 <div className="flex items-center justify-between pt-2">
 <span className="text-xs text-muted-foreground">
 {leads.length} de {pagination.total} leads
 </span>
 <div className="flex gap-2">
 <button
 type="button"
 onClick={() => {
                 fetchLeads(buildLeadParams(pagination.page - 1))
 }}
 disabled={pagination.page === 1}
 className="px-3 py-1.5 text-xs font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
 >
 Anterior
 </button>
 <button
 type="button"
 onClick={() => {
                 fetchLeads(buildLeadParams(pagination.page + 1))
 }}
 disabled={pagination.page === pagination.totalPages}
 className="px-3 py-1.5 text-xs font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
 >
 Próxima
 </button>
 </div>
 </div>
 )}
 </>
 ) : (
 <Card>
 <CardContent className="p-4">
 {isFunnelLoading ? (
 <KanbanSkeleton columns={4} cardsPerColumn={3} />
 ) : (
 <LeadsKanbanView
 funnel={defaultFunnel}
 leads={leads}
 onViewLead={handleOpenDrawer}
 onEditLead={handleEditLead}
 onUpdateLeadStage={handleUpdateLeadStage}
 onAdvanceStageBlocked={handleAdvanceStageBlocked}
 />
 )}
 </CardContent>
 </Card>
 )}
 </div>
 </div>

 {/* Lead Details Drawer */}
 <Drawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} title="Detalhes do Lead" size="lg">
 <LeadDrawer
 lead={drawerLead}
 onEdit={handleEditFromDrawer}
 onContact={handleContactLead}
 onClose={handleCloseDrawer}
 useFullPage={true}
 firstFunnelStageId={firstFunnelStageId}
 salespersonOptions={userComboboxOptions}
 salespersonOptionsLoading={isUsersLoading}
 onAssignSalesperson={handleDrawerAssignSalesperson}
 />
 </Drawer>

 {/* Toast */}
 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />
 )}
 </div>
 )
}
